import React, { FC } from 'react';
import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { FlexCol, H2Text, H3Text } from '@minecraft.atoms';
import { FileInput } from '@minecraft.molecules';
import Icon from '@minecraft.icon';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { useTranslation } from '@blocs.i18n';
import { StyledDragAndDropArea } from './styles';
import { FileTypeWithMeta } from './types';
import { getAllowedFileTypeForMedia, handleDragEnter, handleDragOver, handleFilesUpload } from './helpers';

type MediaUploaderDnDProps = {
  maxFiles?: number;
  currentMediaType: MediaTypeCodeEnum;
  onFilesSelected: (uploadedFilesInfo: { selectedFiles: FileTypeWithMeta[] }) => void;
};

export const MediaUploaderDnD: FC<MediaUploaderDnDProps> = ({ maxFiles, currentMediaType, onFilesSelected }) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const handleFilesUploadInDnD = async (eventFiles: File | File[]) => {
    const result = await handleFilesUpload({ eventFiles, maxFiles, currentMediaType, t });

    if (result?.error) {
      showMessage({
        message: result.error,
        color: 'danger',
      });

      return;
    }

    onFilesSelected({ selectedFiles: result.filesToPreview });
  };

  const handleFilesViaPicker = async (selectedFile: File | File[]) => {
    await handleFilesUploadInDnD(selectedFile);
  };

  const handleFilesViaDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();

    if (!event?.dataTransfer?.files?.length) {
      return;
    }

    await handleFilesUploadInDnD(Array.from(event?.dataTransfer?.files));
  };

  return (
    <StyledDragAndDropArea
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDrop={handleFilesViaDrop}
      data-testid="media-upload-dnd-wrapper"
    >
      <FlexCol
        data-testid="media-upload-matterhorn-dnd-wrapper"
        className="h-100"
        alignItems="center"
        justifyContent="center"
        gap="2"
        textColor="lowContrastText"
      >
        <Icon name="upload-cloud" inheritColor height={6.25} width={6.25} />
        <H2Text as="span" color="lowContrastText" fontWeight="medium" className="mb-3">
          {maxFiles !== 1
            ? t('common:cloudinaryUploader.local.dd_title_multi')
            : t('common:cloudinaryUploader.local.dd_title_single')}
        </H2Text>
        <H3Text as="span" color="disabled" fontWeight="regular">
          {t('common:cloudinaryUploader.or')}
        </H3Text>
        <FileInput
          buttonProps={{
            size: 'small',
            outlined: false,
          }}
          accept={getAllowedFileTypeForMedia(currentMediaType)}
          multiple={maxFiles !== 1}
          value={null}
          onChange={handleFilesViaPicker}
          name="upload-media"
          buttonLabel={t('common:cloudinaryUploader.local.browse')}
          id="upload-media"
        />
      </FlexCol>
    </StyledDragAndDropArea>
  );
};
